// An inventory checklist is walked around with and filled in by pen, so the sheet is portrait a4
// printed as two narrow columns, the categories running one after another through them.
const PAGE = {orientation: 'portrait', unit: 'mm', format: 'a4'};
const FONT = 'helvetica';
const FONT_SIZE = 9;
// small enough that 'LAST WEEK' still heads its narrow column without wrapping onto a third line
const HEAD_FONT_SIZE = 7.5;
// the food id and the other categories an ingredient is in, tucked under its name
const FOOTNOTE_FONT_SIZE = 6;
const HEADING_FONT_SIZE = 11;
const TITLE_FONT_SIZE = 9;
const FOOTER_FONT_SIZE = 8;
const CELL_PADDING = 2;
// jspdf's default, and what autotable assumes when it works out how tall a row has to be
const LINE_HEIGHT_FACTOR = 1.15;
const MM_PER_POINT = 25.4 / 72;
// as little margin as a printer will take, so the two columns keep as much width as possible
const MARGIN = 6;
// the columns the page is printed in - each holds its own table, headed for itself
const BANDS = 2;
const BAND_GAP = 5;
// space above a table for the category heading, and below the page for the footer
const HEADING_HEIGHT = 8;
const HEADING_BASELINE = 4.5;
const FOOTER_HEIGHT = 6;
// a breath between one category and the next, which follows on in the same band
const SECTION_GAP = 5;
// two lines tall even where one would do, so the rows stay an even height down the band
const ROW_HEIGHT = 2 * FONT_SIZE * MM_PER_POINT * LINE_HEIGHT_FACTOR + 2 * CELL_PADDING;
// a heading with only its column headings under it is no use, so start such a category in the next band
const MIN_SECTION_HEIGHT = HEADING_HEIGHT + 3 * ROW_HEIGHT;
const WRITE_IN_WIDTH = 18;
// '999.99kg' is 13.3mm and the heading above it 13.1mm, so both fit on narrower side padding
const AMOUNT_WIDTH = 16;
const NARROW_CELL_PADDING = {top: CELL_PADDING, bottom: CELL_PADDING, left: 1, right: 1};
const LINE_COLOUR = [120, 120, 120];
const FOOTNOTE_COLOUR = [120, 120, 120];
const HEAD_FILL = [225, 225, 225];
// faint tint so the eye can follow a row across the column
const ALTERNATE_ROW_FILL = [245, 245, 245];
// the column holding the amount to buy - kept narrow, with the figures right aligned under each other
const AMOUNT_COLUMN = /needed|amount/i;
// autotable nudges the first line of a cell down by this much before drawing it
const FIRST_LINE_OFFSET = 2 - LINE_HEIGHT_FACTOR;
// eg 'Aug 13, 2026, 3:04 p.m.' - so a sheet found on a bench can be dated
const PRINTED_AT_FORMAT = new Intl.DateTimeFormat('en-CA', {dateStyle: 'medium', timeStyle: 'short'});

// a cell value of {text, note, footnote} prints the note in bold brackets on the end of the text,
// and the footnote small and grey at the foot of the cell
function isNoteCell(value) {
  return !!value && typeof value === 'object';
}

function bracketedNote(note) {
  return note ? `(${note})` : '';
}

function noteCellText({text, note}) {
  return [text, bracketedNote(note)].filter(Boolean).join(' ');
}

// the footnote is drawn by hand in its own small font, so it is no part of what autotable measures
function cellContent(value) {
  return isNoteCell(value) ? noteCellText(value) : (value || '');
}

function hasFootnote(value) {
  return isNoteCell(value) && !!value.footnote;
}

function lineHeight(fontSize) {
  return fontSize * MM_PER_POINT * LINE_HEIGHT_FACTOR;
}

/**
 * @param rows array of objects keyed by column name
 * @return {{columns, writeInColumns, noteColumns, boldColumns}} the column names, those left blank
 * for whoever is counting, those holding a {text, note, footnote} pair, and those drawn in bold
 */
export function checklistColumns(rows) {
  const columns = Object.keys(rows[0] || {});
  return {
    columns,
    writeInColumns: columns.filter(column => rows.every(row => !row[column])),
    noteColumns: columns.filter(column => rows.some(row => isNoteCell(row[column]))),
    // measured in bold, which is the widest any of it will be drawn
    boldColumns: columns.filter(column =>
      AMOUNT_COLUMN.test(column) || rows.some(row => isNoteCell(row[column]) && row[column].note))
  };
}

function fixedWidth(column, {writeInColumns}) {
  if (writeInColumns.includes(column)) {
    return WRITE_IN_WIDTH;
  }
  return AMOUNT_COLUMN.test(column) ? AMOUNT_WIDTH : 0;
}

/**
 * How the columns of a category are laid out in a band: the fixed widths first, with whatever is
 * left shared between the columns that hold words.
 */
function tablePlan(rows, bandWidth) {
  const found = checklistColumns(rows);
  const fixed = found.columns.map(column => fixedWidth(column, found));
  const flexible = fixed.filter(width => width === 0).length;
  const spare = (bandWidth - fixed.reduce((total, width) => total + width, 0)) / (flexible || 1);
  return {
    ...found,
    widths: Object.fromEntries(found.columns.map((column, index) => [column, fixed[index] || spare]))
  };
}

function columnFont(column, {boldColumns}) {
  return boldColumns.includes(column) ? 'bold' : 'normal';
}

function columnStyle(column, plan) {
  const style = {cellWidth: plan.widths[column]};
  if (fixedWidth(column, plan)) {
    style.cellPadding = NARROW_CELL_PADDING;
  }
  if (AMOUNT_COLUMN.test(column)) {
    style.halign = 'right';
  }
  if (plan.boldColumns.includes(column)) {
    style.fontStyle = 'bold';
  }
  if (plan.noteColumns.includes(column)) {
    // drawn by hand from the top of the cell down, with the footnote sat on the bottom of it
    style.valign = 'top';
  }
  return style;
}

// how tall autotable would make a row - measured the same way, so a band is packed with what fits in it
function measureHeight(doc, texts, plan, fontSize, fontStyle) {
  const lines = plan.columns.map((column, index) => {
    const text = `${texts[index]}`;
    if (!text) {
      return 1;
    }
    doc.setFont(FONT, fontStyle || columnFont(column, plan));
    const padding = fixedWidth(column, plan) ? 2 * NARROW_CELL_PADDING.left : 2 * CELL_PADDING;
    return doc.splitTextToSize(text, plan.widths[column] - padding, {fontSize}).length;
  });
  return Math.max(...lines) * lineHeight(fontSize) + 2 * CELL_PADDING;
}

// a row is as tall as its text, plus a small line for the footnote under it, and never under two lines
function rowHeight(doc, values, plan) {
  const footnote = values.some(hasFootnote) ? lineHeight(FOOTNOTE_FONT_SIZE) : 0;
  return Math.max(ROW_HEIGHT, measureHeight(doc, values.map(cellContent), plan, FONT_SIZE) + footnote);
}

/**
 * autotable draws a cell in a single font, so a line that ends in a bold note is drawn by hand.
 * The text is wrapped in the font the column was measured in, so a plainly drawn part of a line can
 * only ever come out narrower than the space set aside for it.
 */
function drawTextAndNote({doc, cell}, value) {
  const full = noteCellText(value);
  // everything from here on is the note, whichever line it lands on
  const noteStart = full.length - bracketedNote(value.note).length;
  const measuredIn = cell.styles.fontStyle || 'normal';

  doc.setFont(FONT, measuredIn);
  // the width autotable wrapped this cell with, rounding error and all
  const width = cell.width - cell.padding('horizontal') + 1 / doc.internal.scaleFactor;
  const lines = doc.splitTextToSize(full, width, {fontSize: cell.styles.fontSize});

  const fontSize = cell.styles.fontSize / doc.internal.scaleFactor;
  const height = fontSize * doc.getLineHeightFactor();
  const {x, y} = cell.getTextPos();
  let lineY = y + fontSize * FIRST_LINE_OFFSET;
  let lineStart = 0;
  lines.forEach(line => {
    // wrapping only drops the spaces it breaks on, so each line is still a slice of the whole
    lineStart = Math.max(full.indexOf(line, lineStart), 0);
    const plain = line.slice(0, Math.max(noteStart - lineStart, 0));
    const bold = line.slice(plain.length);
    let plainWidth = 0;
    if (plain) {
      doc.setFont(FONT, 'normal');
      doc.text(plain, x, lineY);
      plainWidth = doc.getTextWidth(plain);
    }
    if (bold) {
      doc.setFont(FONT, 'bold');
      doc.text(bold, x + plainWidth, lineY);
    }
    lineStart += line.length;
    lineY += height;
  });

  if (value.footnote) {
    doc.setFont(FONT, 'normal');
    doc.setFontSize(FOOTNOTE_FONT_SIZE);
    doc.setTextColor(FOOTNOTE_COLOUR[0], FOOTNOTE_COLOUR[1], FOOTNOTE_COLOUR[2]);
    doc.text(value.footnote, x, cell.y + cell.height - cell.padding('bottom'));
    doc.setFontSize(cell.styles.fontSize);
    doc.setTextColor(0);
  }
}

// the two columns the page is printed in, and the top and bottom the tables run between
function pageLayout(doc) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const width = (pageWidth - 2 * MARGIN - (BANDS - 1) * BAND_GAP) / BANDS;
  return {
    pageWidth,
    width,
    lefts: Array.from({length: BANDS}, (unused, band) => MARGIN + band * (width + BAND_GAP)),
    top: MARGIN,
    bottom: doc.internal.pageSize.getHeight() - MARGIN - FOOTER_HEIGHT
  };
}

// the next column to print in, starting a page when this one was the last of them
function nextBand(doc, layout, band) {
  if (band + 1 < layout.lefts.length) {
    return {band: band + 1, y: layout.top};
  }
  doc.addPage();
  return {band: 0, y: layout.top};
}

function drawHeading(doc, heading, title, {x, y, width}) {
  doc.setTextColor(0);
  doc.setFont(FONT, 'bold');
  doc.setFontSize(HEADING_FONT_SIZE);
  doc.text(heading, x, y + HEADING_BASELINE);
  doc.setFont(FONT, 'normal');
  doc.setFontSize(TITLE_FONT_SIZE);
  doc.text(`${title || ''}`, x + width, y + HEADING_BASELINE, {align: 'right'});
}

/**
 * Head each run of a category, saying which of its runs this is when it takes more than one column -
 * so the sheets of a category that has been carried around still make sense in a pile.
 */
function drawHeadings(doc, {category, title, placements}) {
  const name = `${category || ''}`.toUpperCase();
  placements.forEach((placement, index) => {
    doc.setPage(placement.page);
    drawHeading(doc, placements.length > 1 ? `${name} (${index + 1} of ${placements.length})` : name,
      title, placement);
  });
}

// as many rows as will fit the space left in the band, and always at least one so it cannot stall
function rowsThatFit(heights, from, available) {
  let count = 0;
  let used = 0;
  while (from + count < heights.length && used + heights[from + count] <= available) {
    used += heights[from + count];
    count++;
  }
  return Math.max(count, 1);
}

function tableOptions(plan, {body, values, heights, startY, left, layout}) {
  const drawnByHand = data => data.section === 'body' && plan.noteColumns.includes(plan.columns[data.column.index]);
  return {
    head: [plan.columns],
    body,
    startY,
    tableWidth: layout.width,
    margin: {
      top: layout.top,
      bottom: MARGIN + FOOTER_HEIGHT,
      left,
      right: layout.pageWidth - left - layout.width
    },
    theme: 'grid',
    // move a row that does not fit to the next page rather than cutting it in half
    rowPageBreak: 'avoid',
    styles: {
      fontSize: FONT_SIZE,
      cellPadding: CELL_PADDING,
      minCellHeight: ROW_HEIGHT,
      valign: 'middle',
      overflow: 'linebreak',
      lineColor: LINE_COLOUR,
      lineWidth: 0.2
    },
    headStyles: {
      fontStyle: 'bold', fontSize: HEAD_FONT_SIZE, fillColor: HEAD_FILL, textColor: 0, valign: 'middle'
    },
    alternateRowStyles: {fillColor: ALTERNATE_ROW_FILL},
    columnStyles: Object.fromEntries(plan.columns.map((column, index) =>
      [index, columnStyle(column, plan)])),
    didParseCell(data) {
      const column = plan.columns[data.column.index];
      if (data.section === 'body') {
        // the height the band was packed with, which is the only one that knows about the footnote
        data.cell.styles.minCellHeight = heights[data.row.index];
        // keep the cells that get written into white, whatever the row tint
        if (plan.writeInColumns.includes(column)) {
          data.cell.styles.fillColor = false;
        }
      }
      // column styles only reach body cells, so the narrow headings are padded to match by hand
      if (data.section === 'head' && fixedWidth(column, plan)) {
        data.cell.styles.cellPadding = NARROW_CELL_PADDING;
      }
    },
    willDrawCell(data) {
      if (drawnByHand(data)) {
        data.cell.text = [];
      }
    },
    didDrawCell(data) {
      if (drawnByHand(data)) {
        drawTextAndNote(data, values[data.row.index][data.column.index]);
      }
    }
  };
}

/**
 * Print one category, breaking it into as many column-fulls as it takes, and return where the next
 * category can start.
 */
function drawSection(doc, autoTable, {category, title, rows}, layout, {band, y}) {
  const plan = tablePlan(rows, layout.width);
  const values = rows.map(row => plan.columns.map(column => row[column]));
  const body = values.map(row => row.map(cellContent));
  const heights = values.map(row => rowHeight(doc, row, plan));
  const headHeight = Math.max(ROW_HEIGHT, measureHeight(doc, plan.columns, plan, HEAD_FONT_SIZE, 'bold'));
  const placements = [];

  let index = 0;
  while (index < rows.length) {
    if (layout.bottom - y < MIN_SECTION_HEIGHT) {
      ({band, y} = nextBand(doc, layout, band));
    }
    const left = layout.lefts[band];
    const count = rowsThatFit(heights, index, layout.bottom - y - HEADING_HEIGHT - headHeight);
    placements.push({page: doc.internal.getNumberOfPages(), x: left, y, width: layout.width});
    autoTable(doc, tableOptions(plan, {
      body: body.slice(index, index + count),
      values: values.slice(index, index + count),
      heights: heights.slice(index, index + count),
      startY: y + HEADING_HEIGHT,
      left,
      layout
    }));
    index += count;
    y = ((doc.lastAutoTable || {}).finalY || layout.bottom) + SECTION_GAP;
    if (index < rows.length) {
      ({band, y} = nextBand(doc, layout, band));
    }
  }

  // headed once the tables are down, when how many columns the category took is known
  drawHeadings(doc, {category, title, placements});
  return {band, y};
}

function drawFooters(doc, printedAt) {
  const pages = doc.internal.getNumberOfPages();
  const width = doc.internal.pageSize.getWidth();
  const height = doc.internal.pageSize.getHeight();
  doc.setFont(FONT, 'normal');
  doc.setFontSize(FOOTER_FONT_SIZE);
  doc.setTextColor(120);
  for (let page = 1; page <= pages; page++) {
    doc.setPage(page);
    doc.text(`printed ${printedAt}`, MARGIN, height - 2);
    doc.text(`page ${page} of ${pages}`, width - MARGIN, height - 2, {align: 'right'});
  }
}

/**
 * Save the same groups csvExportGrouped writes as a printable checklist pdf - one category after
 * another through two columns a page, with the columns nobody has filled in left as empty boxes to
 * write into.
 * @param groups [{category, title, rows}] where rows is an array of objects keyed by column name
 * @param filename name of the downloaded file
 */
export async function exportChecklistPdf(groups, filename) {
  const sections = groups.filter(({rows}) => rows && rows.length > 0);
  if (sections.length === 0) {
    alert('no data to export');
    return;
  }

  // loaded on demand - the pdf libraries are far bigger than the rest of the page
  const [{jsPDF}, {default: autoTable}] = await Promise.all([
    import(/* webpackChunkName: "pdf-export" */ 'jspdf'),
    import(/* webpackChunkName: "pdf-export" */ 'jspdf-autotable')
  ]);

  const doc = new jsPDF(PAGE);
  doc.setProperties({title: filename});
  const layout = pageLayout(doc);
  let cursor = {band: 0, y: layout.top};
  sections.forEach(section => {
    cursor = drawSection(doc, autoTable, section, layout, cursor);
  });
  drawFooters(doc, PRINTED_AT_FORMAT.format(new Date()));
  doc.save(filename);
}
